import { MouseEvent as ReactMouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ChatCircleTextIcon, FlagIcon, PaperPlaneRightIcon } from "@phosphor-icons/react";
import { isEnterSubmit } from "@libs/zapi-ui/custom";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useTranslation } from "react-i18next";
import { FlagDeficiencyDialog } from "@/zapi-frontend/features/FlagDeficiency";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";
import { scrollIntoView } from "@/zapi-frontend/shared/lib/scrollIntoView";
import { describeSelectionAnchor, TTextQuoteAnchor } from "@/zapi-frontend/shared/lib/textQuoteAnchor";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useCommentStore } from "@/zapi-frontend/widgets/CommentsIsland/model/store/commentsStore";
import { useCommentAnchorsStore } from "@/zapi-frontend/widgets/CommentsIsland/model/store/commentAnchorsStore";
import { TComment } from "@/zapi-frontend/widgets/CommentsIsland/model/types/types";
import { generateStubComment } from "@/zapi-frontend/widgets/CommentsIsland/lib/generateStubComment";
import { useCommentHighlights } from "@/zapi-frontend/widgets/CommentsIsland/lib/useCommentHighlights";
import {
  findRangeAtPoint,
  setPendingCommentHighlight
} from "@/zapi-frontend/widgets/CommentsIsland/lib/commentHighlights";

const TOOLBAR_WIDTH = 200;
const COMPOSER_WIDTH = 300;
const NO_COMMENTS: TComment[] = [];

type TSelectionToolbar = { top: number; left: number; range: Range; quote: string };
type TComposer = { top: number; left: number; anchor: TTextQuoteAnchor };

type TBlocksSelectionAreaProps = {
  projectId: number;
  chapterId: number;
  contentKey: string;
  children: ReactNode;
  className?: string;
};

// Selection affordances for read-only block chapters, where the TipTap selection toolbar never
// mounts: select text, then Comment (anchored by quote, see textQuoteAnchor) or Flag a deficiency.
export function BlocksSelectionArea({ projectId, chapterId, contentKey, children, className }: TBlocksSelectionAreaProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hoverFrameRef = useRef<number | null>(null);
  const [toolbar, setToolbar] = useState<TSelectionToolbar | null>(null);
  const [composer, setComposer] = useState<TComposer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFlagDialogOpen, setIsFlagDialogOpen] = useState(false);
  const [flagQuote, setFlagQuote] = useState("");

  const { id: userId, firstName, lastName } = useMeStore();
  const { openHandler, scrollToBottomCallback } = useProjectSidebarStore();
  const { runTimeout } = useScopedTimeout();
  const projectKey = String(projectId);
  const chapterKey = String(chapterId);
  const fetchComments = useCommentStore((s) => s.fetchComments);
  const createComment = useCommentStore((s) => s.createComment);
  const comments = useCommentStore((s) => s.commentsStorage[chapterKey] ?? NO_COMMENTS);
  const anchorEntries = useCommentAnchorsStore((s) => s.entries);

  useEffect(() => {
    fetchComments(projectKey, chapterKey);
  }, [fetchComments, projectKey, chapterKey]);

  useCommentHighlights(containerRef, comments, contentKey);

  const syncToolbar = useCallback(() => {
    if (composer) return;
    const selection = window.getSelection();
    const container = containerRef.current;
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const quote = selection?.toString().trim() ?? "";
    if (!selection || selection.isCollapsed || !range || !quote || !container?.contains(range.commonAncestorContainer)) {
      setToolbar(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    setToolbar({ top: rect.top, left: rect.left + rect.width / 2, range: range.cloneRange(), quote });
  }, [composer]);

  // On touch the selection settles a frame after touchend.
  const handleTouchEnd = () => requestAnimationFrame(() => requestAnimationFrame(syncToolbar));

  useEffect(() => {
    if (!toolbar) return;
    const hide = () => setToolbar(null);
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [toolbar]);

  const closeComposer = useCallback(() => {
    setPendingCommentHighlight(null);
    setComposer(null);
  }, []);

  useEffect(() => {
    if (!composer) return;
    runTimeout(() => inputRef.current?.focus(), 0);
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("[data-blocks-comment-composer]")) return;
      closeComposer();
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [composer, closeComposer, runTimeout]);

  const openComposer = () => {
    const container = containerRef.current;
    if (!toolbar || !container) return;
    const described = describeSelectionAnchor(container, toolbar.range);
    setToolbar(null);
    if (!described) return;
    const { startContainer } = toolbar.range;
    const startElement = startContainer instanceof Element ? startContainer : startContainer.parentElement;
    const sectionId = startElement?.closest<HTMLElement>("[data-section-id]")?.dataset.sectionId;
    setPendingCommentHighlight(toolbar.range);
    setComposer({ top: toolbar.top, left: toolbar.left, anchor: sectionId ? { ...described, sectionId } : described });
  };

  const openFlagDialog = () => {
    if (!toolbar) return;
    setFlagQuote(toolbar.quote);
    setToolbar(null);
    setIsFlagDialogOpen(true);
  };

  const scrollToCommentCard = useCallback(
    (commentId: string, attempt = 0) => {
      const card = document.getElementById(commentId);
      if (!card) {
        if (attempt < 20) runTimeout(() => scrollToCommentCard(commentId, attempt + 1), 100);
        return;
      }
      scrollIntoView(card, () => {
        card.classList.add("highlighted");
        runTimeout(() => card.classList.remove("highlighted"), 1000);
      });
    },
    [runTimeout]
  );

  const submitComment = async () => {
    const content = inputRef.current?.value.trim() ?? "";
    if (!composer || !content || !userId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await createComment({
        projectId: projectKey,
        chapterId: chapterKey,
        content,
        anchor: composer.anchor,
        stubComment: generateStubComment({ userId, firstName, lastName, content })
      });
      closeComposer();
      openHandler("comments");
      runTimeout(() => scrollToBottomCallback?.(), 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  // A click on highlighted text opens its comment, like a click on a persistent mark in the editor.
  const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (anchorEntries.length === 0 || !window.getSelection()?.isCollapsed) return;
    const entry = findRangeAtPoint(anchorEntries, event.clientX, event.clientY);
    if (!entry) return;
    openHandler("comments");
    runTimeout(() => scrollToCommentCard(entry.commentId), 200);
  };

  const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (anchorEntries.length === 0 || hoverFrameRef.current !== null) return;
    const { clientX, clientY } = event;
    hoverFrameRef.current = requestAnimationFrame(() => {
      hoverFrameRef.current = null;
      const container = containerRef.current;
      if (!container) return;
      container.style.cursor = findRangeAtPoint(anchorEntries, clientX, clientY) ? "pointer" : "";
    });
  };

  const clampLeft = (left: number, width: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    const min = (rect?.left ?? 0) + 8;
    const max = (rect?.right ?? window.innerWidth) - width - 8;
    return Math.max(min, Math.min(left, max));
  };

  return (
    <div
      ref={containerRef}
      className={className}
      onMouseUp={syncToolbar}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
    >
      {children}
      {toolbar && !composer && !isFlagDialogOpen && (
        <div
          className="fixed z-1000 flex items-center gap-1 rounded-lg border border-innoveraZinc-150 bg-white px-1 py-1 shadow-lg"
          style={{ top: Math.max(8, toolbar.top - 46), left: clampLeft(toolbar.left - TOOLBAR_WIDTH / 2, TOOLBAR_WIDTH) }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <button
            type="button"
            data-testid="blocks-selection-comment-button"
            className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[14px] font-medium text-zinc-900 hover:bg-zinc-100"
            onClick={openComposer}
          >
            <ChatCircleTextIcon className="h-4 w-4" />
            Comment
          </button>
          <div className="h-4 w-px bg-zinc-200" />
          <button
            type="button"
            data-testid="blocks-selection-flag-button"
            className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[14px] font-medium text-zinc-900 hover:bg-zinc-100"
            onClick={openFlagDialog}
          >
            <FlagIcon className="h-4 w-4" />
            Flag
          </button>
        </div>
      )}
      {composer && (
        <div
          data-blocks-comment-composer
          className="fixed z-1000 rounded-md border border-gray-200 bg-white p-2 shadow-lg"
          style={{
            top: Math.max(8, composer.top - 46),
            left: clampLeft(composer.left - COMPOSER_WIDTH / 2, COMPOSER_WIDTH),
            width: COMPOSER_WIDTH
          }}
        >
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              data-testid="blocks-inline-comment-input"
              className="w-full rounded border border-gray-300 px-2 py-1 text-[14px] focus:border-transparent focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="Type your comment..."
              disabled={isSubmitting}
              onKeyDown={(e) => {
                if (isEnterSubmit(e, { allowShift: true })) {
                  e.preventDefault();
                  void submitComment();
                } else if (e.key === "Escape") {
                  closeComposer();
                }
              }}
            />
            {isSubmitting ? (
              <Spinner aria-label={t("loading")} className="size-5 shrink-0 text-muted-foreground" />
            ) : (
              <button
                type="button"
                data-testid="blocks-inline-comment-submit-button"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-zinc-900 hover:bg-zinc-100"
                aria-label="Submit comment"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void submitComment()}
              >
                <PaperPlaneRightIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}
      <FlagDeficiencyDialog
        isOpen={isFlagDialogOpen}
        onOpenChange={setIsFlagDialogOpen}
        target={{ kind: "memoChapter", projectId, chapterId }}
        quote={flagQuote}
      />
    </div>
  );
}
